import React, { useState, useEffect } from 'react';
import Sidebar from '../Layouts/Sidebar';
import Topbar from '../Layouts/Topbar';
import './Reviews.css';

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [filteredReviews, setFilteredReviews] = useState([]);
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'replied'
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedReview, setSelectedReview] = useState(null);
  const [replyText, setReplyText] = useState('');

  // Mock data - replace with actual API call
  useEffect(() => {
    // Simulating API call
    const mockReviews = [
      {
        id: 1,
        user: {
          id: 1,
          name: 'John Doe',
          avatar: 'https://ui-avatars.com/api/?name=John+Doe&background=random',
          role: 'tenant'
        },
        property: {
          id: 1,
          title: 'Modern Apartment in City Center',
          image: '/images/property1.jpg'
        },
        rating: 4.5,
        comment: 'Great property, very clean and well-maintained. The landlord is very responsive.',
        status: 'pending',
        date: '2024-03-15',
        reply: null
      },
      {
        id: 2,
        user: {
          id: 2,
          name: 'Jane Smith',
          avatar: 'https://ui-avatars.com/api/?name=Jane+Smith&background=random',
          role: 'tenant'
        },
        property: {
          id: 2,
          title: 'Cozy Studio Apartment',
          image: '/images/property2.jpg'
        },
        rating: 5,
        comment: 'Excellent experience! The property exceeded my expectations.',
        status: 'replied',
        date: '2024-03-14',
        reply: {
          text: 'Thank you for your kind words! We\'re glad you enjoyed your stay.',
          date: '2024-03-14'
        }
      },
      // Add more mock reviews as needed
    ];

    setReviews(mockReviews);
    setFilteredReviews(mockReviews);
    setLoading(false);
  }, []);

  // Filter reviews based on selected filter and search term
  useEffect(() => {
    let filtered = reviews;

    // Apply status filter
    if (filter !== 'all') {
      filtered = filtered.filter(review => review.status === filter);
    }

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(review => 
        review.user.name.toLowerCase().includes(searchLower) ||
        review.property.title.toLowerCase().includes(searchLower) ||
        review.comment.toLowerCase().includes(searchLower)
      );
    }

    setFilteredReviews(filtered);
  }, [filter, searchTerm, reviews]);

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleReply = (review) => {
    setSelectedReview(review);
    setReplyText(review.reply?.text || '');
  };

  const handleSubmitReply = () => {
    if (!replyText.trim()) return;

    // Update the review with the reply
    const updatedReviews = reviews.map(review => {
      if (review.id === selectedReview.id) {
        return {
          ...review,
          status: 'replied',
          reply: {
            text: replyText,
            date: new Date().toISOString().split('T')[0]
          }
        };
      }
      return review;
    });

    setReviews(updatedReviews);
    setSelectedReview(null);
    setReplyText('');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return '#ff9800';
      case 'replied':
        return '#4caf50';
      default:
        return '#666';
    }
  };

  return (
    <div className="admin-reviews">
      <Sidebar />
      <Topbar />
      
      <div className="reviews-content">
        <div className="reviews-header">
          <h1>Reviews Management</h1>
          <p>Manage and respond to user reviews</p>
        </div>

        <div className="reviews-filters">
          <div className="search-box">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Search reviews..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>

          <div className="filter-buttons">
            <button
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => handleFilterChange('all')}
            >
              All Reviews
            </button>
            <button
              className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
              onClick={() => handleFilterChange('pending')}
            >
              Pending
            </button>
            <button
              className={`filter-btn ${filter === 'replied' ? 'active' : ''}`}
              onClick={() => handleFilterChange('replied')}
            >
              Replied
            </button>
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading reviews...</div>
        ) : (
          <div className="reviews-list">
            {filteredReviews.map(review => (
              <div key={review.id} className="review-card">
                <div className="review-header">
                  <div className="reviewer-info">
                    <img
                      src={review.user.avatar}
                      alt={review.user.name}
                      className="reviewer-avatar"
                    />
                    <div>
                      <h3>{review.user.name}</h3>
                      <span className="reviewer-role">{review.user.role}</span>
                    </div>
                  </div>
                  <div className="review-meta">
                    <span className="review-date">{new Date(review.date).toLocaleDateString()}</span>
                    <span
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(review.status) }}
                    >
                      {review.status}
                    </span>
                  </div>
                </div>

                <div className="property-info">
                  <img
                    src={review.property.image}
                    alt={review.property.title}
                    className="property-image"
                  />
                  <h4>{review.property.title}</h4>
                </div>

                <div className="review-content">
                  <div className="rating">
                    {[...Array(5)].map((_, index) => (
                      <i
                        key={index}
                        className={`fas fa-star ${index < review.rating ? 'active' : ''}`}
                      ></i>
                    ))}
                    <span>{review.rating}</span>
                  </div>
                  <p className="review-comment">{review.comment}</p>
                </div>

                {review.reply && (
                  <div className="review-reply">
                    <div className="reply-header">
                      <i className="fas fa-reply"></i>
                      <span>Admin Reply</span>
                      <span className="reply-date">{new Date(review.reply.date).toLocaleDateString()}</span>
                    </div>
                    <p>{review.reply.text}</p>
                  </div>
                )}

                {review.status === 'pending' && (
                  <button
                    className="reply-button"
                    onClick={() => handleReply(review)}
                  >
                    <i className="fas fa-reply"></i>
                    Reply
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Reply Modal */}
        {selectedReview && (
          <div className="reply-modal">
            <div className="reply-modal-content">
              <div className="reply-modal-header">
                <h3>Reply to Review</h3>
                <button
                  className="close-button"
                  onClick={() => setSelectedReview(null)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="reply-modal-body">
                <div className="original-review">
                  <p><strong>Original Review:</strong></p>
                  <p>{selectedReview.comment}</p>
                </div>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Write your reply..."
                  rows="4"
                ></textarea>
              </div>
              <div className="reply-modal-footer">
                <button
                  className="cancel-button"
                  onClick={() => setSelectedReview(null)}
                >
                  Cancel
                </button>
                <button
                  className="submit-button"
                  onClick={handleSubmitReply}
                >
                  Submit Reply
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reviews;
